import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
    Alert,
    CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import { profileModel, reportModel } from '../Axios/AxiosConfig';

const SendNotificationPage: React.FC = () => {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [image, setImage] = useState('');
    const [deeplink, setDeeplink] = useState('');
    const [targetUID, setTargetUID] = useState('');
    const [isClinical, setIsClinical] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!title.trim() || !body.trim()) {
            setError("Title and Body are required.");
            return;
        }

        setLoading(true);

        try {
            const payload = {
                title: title.trim(),
                body: body.trim(),
                image: image.trim() || undefined,
                deeplink: deeplink.trim() || undefined,
                targetUID: targetUID.trim() || undefined,
                isClinical: isClinical
            };

            const response = await reportModel.post('/sendCustomNotification', payload);

            if (response.status === 200) {
                toast.success(`Notification sent! Success: ${response.data.success}, Failed: ${response.data.failed}`);
                // Reset form optionally, or keep for reuse
                // setTitle('');
                // setBody('');
            } else {
                toast.error("Failed to send notification.");
            }
        } catch (err: any) {
            console.error("Error sending notification:", err);
            setError(err?.response?.data?.message || err.message || "An error occurred.");
            toast.error("An error occurred while sending the notification.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    Send Custom Notification
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Send push notifications to users or a specific target.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Card sx={{ maxWidth: 800 }}>
                <CardContent sx={{ p: 3 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Title"
                                    fullWidth
                                    required
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Body"
                                    fullWidth
                                    required
                                    multiline
                                    rows={4}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Image URL (Optional)"
                                    fullWidth
                                    value={image}
                                    onChange={(e) => setImage(e.target.value)}
                                    helperText="URL of the image to display in the notification"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Deep Link (Optional)"
                                    fullWidth
                                    value={deeplink}
                                    onChange={(e) => setDeeplink(e.target.value)}
                                    helperText="App deep link (e.g., /home)"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Target UID (Optional)"
                                    fullWidth
                                    value={targetUID}
                                    onChange={(e) => setTargetUID(e.target.value)}
                                    helperText="Send to a specific user only. Overrides 'Is Clinical'."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isClinical}
                                            onChange={(e) => setIsClinical(e.target.checked)}
                                            disabled={!!targetUID}
                                        />
                                    }
                                    label="Send to Clinical Users"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={loading}
                                    sx={{ minWidth: 150 }}
                                >
                                    {loading ? <CircularProgress size={24} /> : "Send Notification"}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SendNotificationPage;
